import {
  FailedGroup,
  FailedMediaListModal,
  FailedRow,
} from '@components/FailedMediaList/FailedMediaListModal';
import { getFileThumbnailUrl } from '@lib/hooks/useFileMetadataExtraction';
import { errorIsNetwork } from '@lib/utils/errorIsNetwork';
import { isAudioFile, isAudioMedia } from '@lib/utils/fileExtensions';
import { showToast } from '@lib/utils/toastmaster';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { BatchNotification, isBatchNotification, notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';

const OLDER_GROUP_KEY = '__older__';
const RETRY_ALL_COOLDOWN_MS = 5000;

interface LibraryFailedMediaModalProps {
  open: boolean;
  onClose: () => void;
}

export const LibraryFailedMediaModal = observer(function LibraryFailedMediaModal({
  open,
  onClose,
}: LibraryFailedMediaModalProps) {
  const folder = useLibraryFolder();
  const uploads = useUploadManager();
  const [isDeleting, setIsDeleting] = useState(false);
  const [retryAllCooldown, setRetryAllCooldown] = useState(false);
  const previousLibraryIdRef = useRef<string | undefined>(undefined);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the cooldown timer on unmount so it can't flip state on an
  // unmounted component. Required by the timer-cleanup rule.
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, []);

  // No registration here: the shared FailedMediaListModal shell this renders registers
  // 'failed-media' itself, which covers this surface along with the four others.

  // Drop rejected files from a previous library when the user navigates between
  // libraries — same behaviour as the prior FailedListModal.
  useEffect(() => {
    const currentLibraryId = folder.library.libraryId;
    if (
      previousLibraryIdRef.current !== undefined &&
      previousLibraryIdRef.current !== currentLibraryId
    ) {
      uploads.rejectedFiles = [];
    }
    previousLibraryIdRef.current = currentLibraryId;
  }, [folder.library.libraryId, uploads]);

  // Index notifications so we can resolve batch context for both errored media
  // and rejected files. Notifications are 30-day-evicted, so older items
  // may not find a match — those fall into the "Older failures" group.
  const batchNotifications = notificationStore.notifications.filter(isBatchNotification);
  const notifById = new Map<string, BatchNotification>();
  const notifByMediaId = new Map<string, BatchNotification>();
  for (const n of batchNotifications) {
    notifById.set(n.id, n);
    for (const mid of n.mediaIds) {
      notifByMediaId.set(mid, n);
    }
  }

  const groupsByKey = new Map<string, FailedGroup>();
  const ensureGroup = (n: BatchNotification | null): FailedGroup => {
    const key = n?.id ?? OLDER_GROUP_KEY;
    let group = groupsByKey.get(key);
    if (!group) {
      group = {
        key,
        batchName: n?.title,
        batchTimestamp: n?.timestamp,
        rows: [],
      };
      groupsByKey.set(key, group);
    }
    return group;
  };

  const libraryId = folder.library.data?._id;

  const deleteMediaRow = async (mediaId: string, filename: string) => {
    try {
      await folder.deleteMedia(mediaId);
    } catch (error) {
      console.error('Error deleting failed media:', error);
      showToast('media-delete-error-single', {
        filename,
        libraryName: folder.library.name,
        isNetworkError: errorIsNetwork(error),
      });
    }
  };

  // Errored backend media — retryable via the library endpoint.
  for (const media of folder.filesFailed) {
    const notif = notifByMediaId.get(media._id) ?? null;
    const filename = media.metadata?.name ?? media.metadata?.filename ?? 'Untitled';
    const row: FailedRow = {
      key: `media:${media._id}`,
      filename,
      reason: 'Processing failed',
      status: 'failed',
      isAudio: isAudioMedia(media),
      thumbnailUrl: null,
      onRetry: libraryId
        ? async () => {
            trackingStore.mediaRetried({ mediaId: media._id, libraryId });
            folder.videoSelection.deselectItems([media._id]);
            const result = await folder.retryMedia(libraryId, media._id);
            if ('error' in result) {
              showToast('media-retry-failed', { error: result.message });
            } else {
              folder.refreshMediaById(media._id);
            }
          }
        : undefined,
      onRemove: () => deleteMediaRow(media._id, filename),
      removeAction: 'delete',
    };
    ensureGroup(notif).rows.push(row);
  }

  for (const media of folder.filesAbandonedUploads) {
    const filename = media.metadata?.name ?? media.metadata?.filename ?? 'Untitled';
    const row: FailedRow = {
      key: `media:${media._id}`,
      filename,
      reason: "Upload didn't finish. Upload the file again to add it.",
      status: 'unfinished',
      isAudio: isAudioMedia(media),
      thumbnailUrl: null,
      onRemove: () => deleteMediaRow(media._id, filename),
      removeAction: 'delete',
    };
    ensureGroup(notifByMediaId.get(media._id) ?? null).rows.push(row);
  }

  // Upload-phase rejects — clear-only in library mode (retrying months later
  // would just hit the same constraint, e.g. unsupported format / quota).
  uploads.rejectedFiles.forEach((rf, i) => {
    const notif = rf.notificationId ? (notifById.get(rf.notificationId) ?? null) : null;
    const row: FailedRow = {
      key: `reject:${i}:${rf.file.name}`,
      filename: rf.file.name,
      reason: rf.reason,
      status: (rf.status ?? 'failed') === 'cancelled' ? 'cancelled' : 'failed',
      isAudio: isAudioFile(rf.file.name),
      thumbnailUrl: getFileThumbnailUrl(rf.file),
      onRemove: () => uploads.dismissRejectedFiles((r) => r === rf),
      removeAction: rf.createdMedia ? 'delete' : 'dismiss',
    };
    ensureGroup(notif).rows.push(row);
  });

  const groups: FailedGroup[] = Array.from(groupsByKey.values()).sort((a, b) => {
    // "Older failures" (no timestamp) sorts to the bottom.
    if (a.batchTimestamp === undefined) return 1;
    if (b.batchTimestamp === undefined) return -1;
    return b.batchTimestamp - a.batchTimestamp;
  });

  const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);
  const couldClearAll = totalRows > 0;
  const metaText = totalRows
    ? `${totalRows} ${totalRows === 1 ? 'item needs' : 'items need'} attention`
    : 'Nothing to attend to';

  const handleRetryAll = async () => {
    trackingStore.mediaRetryAllClicked({
      libraryId: folder.library.libraryId,
      failedCount: folder.filesFailed.length,
    });
    // Start the post-response cooldown immediately. `isRetryingAllMedia`
    // already blocks reentry during the in-flight request; the cooldown
    // throttles spam-clicks in the window after the API resolves and
    // before the UI catches up to the new state.
    setRetryAllCooldown(true);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = setTimeout(() => {
      setRetryAllCooldown(false);
      cooldownTimerRef.current = null;
    }, RETRY_ALL_COOLDOWN_MS);

    const success = await folder.retryAllFailedMedia();
    if (success) onClose();
  };

  const handleClearAll = async () => {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      const deletedMediaIds = new Set(folder.filesFailedOrAbandoned.map((m) => m._id));
      const success = await folder.deleteFailedMedia();
      if (success) {
        uploads.dismissRejectedFiles(() => true);
        uploads.cleanupCompletedUploads(deletedMediaIds);
        showToast('failed-media-clear-success');
        onClose();
      } else {
        showToast('failed-media-clear-error');
      }
    } catch (error) {
      console.error(error);
      showToast('failed-media-clear-error-unexpected');
    } finally {
      setIsDeleting(false);
    }
  };

  const retryAllDisabled =
    folder.isRetryingAllMedia || folder.filesFailed.length === 0 || retryAllCooldown;

  return (
    <FailedMediaListModal
      open={open}
      onClose={onClose}
      title="Failed media"
      metaText={metaText}
      groups={groups}
      onRetryAll={folder.filesFailed.length > 0 ? handleRetryAll : undefined}
      retryAllLabel={folder.isRetryingAllMedia ? 'Retrying...' : 'Retry all'}
      retryAllDisabled={retryAllDisabled}
      onClearAll={handleClearAll}
      clearAllLabel={isDeleting ? 'Clearing...' : 'Clear all'}
      clearAllDisabled={isDeleting || !couldClearAll}
    />
  );
});
